import { motion } from "framer-motion";

/* ==================================================================
 * Sketches — the theatre rehearsal notebook illustration system.
 *
 *   FIGURES  (hand-drawn charcoal PNGs, transparent background)
 *   ------------------------------------------------------------
 *   FigureStanding   → /sketches/figure-stand.webp    (grounded stance)
 *   FigureReaching   → /sketches/figure-reach.webp    (raised arm, presence)
 *   GestureHand      → /sketches/figure-gesture.webp  (open-handed speaker)
 *   Spotlight        → /sketches/figure-present.webp  (presenting figure + optional light cone)
 *   PortraitSketch   → /sketches/portrait-sketch.webp (bust portrait placeholder)
 *
 *   SCHEMATICS (SVG — annotated notebook diagrams, not "figures")
 *   ------------------------------------------------------------
 *   StageMap, StageMapChapters, VoiceWave, BreathDiagram,
 *   MovementPath, AudienceSketch, ScriptLines, Curtains
 * ================================================================== */

/* -------------------- PNG figure primitive --------------------
   Renders a single <motion.img>. The wide PNGs have transparent margins,
   so we apply an aspect-ratio + object-cover to crop those margins away
   and let the figure fill its container properly. Callers keep control
   over the box size via className (w-*, h-*, max-h-*, absolute, etc.).
------------------------------------------------------------------ */
const PngFigure = ({
  src,
  alt,
  className = "",
  animate = true,
  invert = false,
  aspect = "aspect-[3/5]",
  objectPosition = "center",
  style,
}) => (
  <motion.img
    src={src}
    alt={alt}
    draggable={false}
    className={`${className} ${aspect} object-cover select-none`}
    style={{
      objectPosition,
      filter: invert
        ? "invert(1) brightness(1.08) contrast(1.05)"
        : "contrast(1.05)",
      mixBlendMode: invert ? "screen" : "multiply",
      ...style,
    }}
    initial={animate ? { opacity: 0, y: 14 } : false}
    whileInView={animate ? { opacity: 1, y: 0 } : undefined}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
    aria-hidden="true"
  />
);

/* -------- FIGURES (PNG) -------- */
export const FigureStanding = ({ className = "", animate = true, invert = false }) => (
  <PngFigure
    src="/sketches/figure-stand.webp"
    alt="Rehearsal sketch — grounded standing figure"
    className={className}
    animate={animate}
    invert={invert}
    aspect="aspect-[2/3]"
  />
);

export const FigureReaching = ({ className = "", animate = true, invert = false }) => (
  <PngFigure
    src="/sketches/figure-reach.webp"
    alt="Rehearsal sketch — figure reaching upward"
    className={className}
    animate={animate}
    invert={invert}
    aspect="aspect-[3/4]"
    objectPosition="55% center"
  />
);

export const GestureHand = ({ className = "", animate = true, invert = false }) => (
  <PngFigure
    src="/sketches/figure-gesture.webp"
    alt="Rehearsal sketch — speaker with open-handed gesture"
    className={className}
    animate={animate}
    invert={invert}
    aspect="aspect-[3/4]"
    objectPosition="55% center"
  />
);

export const PortraitSketch = ({ className = "", animate = true, invert = false }) => (
  <PngFigure
    src="/sketches/portrait-sketch.webp"
    alt="Charcoal portrait sketch — placeholder for coach photograph"
    className={className}
    animate={animate}
    invert={invert}
    aspect="aspect-[4/5]"
  />
);

/* -------- GESTURE CUBE (PNG) --------
   Wide notebook sketch: standing presenter + 3x3x3 gesture cube (27 dots),
   dashed terracotta gesture paths, hand-lettered annotations.
   Used in the methodology chapter on body language & presence.
------------------------------------------------------------------ */
export const GestureCube = ({ className = "", animate = true, invert = false }) => (
  <motion.img
    src="/sketches/gesture-cube.webp"
    alt="Rehearsal sketch — a presenter inside a 3×3×3 gesture cube with 27 hand positions, dashed gesture paths and handwritten notes on posture, presence and expression"
    draggable={false}
    data-testid="gesture-cube-sketch"
    className={`${className} w-full h-auto select-none`}
    style={{
      filter: invert
        ? "invert(1) brightness(1.08) contrast(1.05)"
        : "contrast(1.05)",
      mixBlendMode: invert ? "screen" : "multiply",
    }}
    initial={animate ? { opacity: 0, y: 14 } : false}
    whileInView={animate ? { opacity: 1, y: 0 } : undefined}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
    aria-hidden="true"
  />
);

/* -------- SPOTLIGHT (PNG figure + warm light cone overlay) --------
   Outer div gets the caller-supplied sizing/positioning classes.
   Inner div is always `relative overflow-hidden w-full h-full` so
   the absolutely-positioned children (light cone + figure) render
   correctly no matter how the outer is placed (block, absolute, etc.).
------------------------------------------------------------------ */
export const Spotlight = ({ className = "", animate = true, label = true, invert = false }) => (
  <div className={className} aria-hidden="true">
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: invert
            ? "radial-gradient(ellipse 55% 60% at 50% 22%, rgba(216,155,60,0.22) 0%, rgba(216,155,60,0.06) 48%, rgba(216,155,60,0) 74%)"
            : "radial-gradient(ellipse 55% 60% at 50% 22%, rgba(216,155,60,0.30) 0%, rgba(216,155,60,0.09) 48%, rgba(216,155,60,0) 74%)",
        }}
      />
      <motion.img
        src="/sketches/figure-present.webp"
        alt="Rehearsal sketch — presenter under a stage light"
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain select-none"
        style={{
          objectPosition: "center bottom",
          filter: invert
            ? "invert(1) brightness(1.08) contrast(1.05)"
            : "contrast(1.05)",
          mixBlendMode: invert ? "screen" : "multiply",
        }}
        initial={animate ? { opacity: 0, y: 14 } : false}
        whileInView={animate ? { opacity: 1, y: 0 } : undefined}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden="true"
      />
      {label && (
        <span className="absolute top-3 left-1/2 -translate-x-1/2 font-hand text-terracotta text-lg md:text-xl whitespace-nowrap">
          the light finds you
        </span>
      )}
    </div>
  </div>
);

/* ==================================================================
 *  SCHEMATIC DIAGRAMS (SVG) — notebook annotations, not figures.
 *  Kept intentionally hand-drawn / rough as legitimate script marks.
 * ================================================================== */

const base = {
  fill: "none",
  strokeWidth: 2.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  stroke: "currentColor",
};
const rough = { filter: "url(#pencil-rough)" };
const roughStrong = { filter: "url(#pencil-rough-strong)" };

const drawGroup = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const drawPath = {
  hidden: { pathLength: 0, opacity: 0 },
  show: { pathLength: 1, opacity: 1, transition: { duration: 0.8, ease: "easeInOut" } },
};

const Svg = ({ animate, children, viewBox, className }) => (
  <motion.svg
    viewBox={viewBox}
    className={className}
    aria-hidden="true"
    preserveAspectRatio="xMidYMid meet"
    initial={animate ? "hidden" : false}
    whileInView={animate ? "show" : undefined}
    viewport={{ once: true, margin: "-60px" }}
    variants={animate ? drawGroup : undefined}
  >
    {children}
  </motion.svg>
);
const P = (props) => <motion.path variants={drawPath} {...base} {...props} />;

const Hand = ({ x, y, size = 20, color = "text-ink", children, anchor = "start" }) => (
  <text x={x} y={y} textAnchor={anchor} className={`font-hand fill-current ${color}`} style={{ fontSize: size }}>
    {children}
  </text>
);

/* Small audience person icon (head + shoulders) */
const person = (x, y, r = 8) =>
  `M${x} ${y} m-${r} 0 a${r} ${r} 0 1 0 ${r * 2} 0 a${r} ${r} 0 1 0 -${r * 2} 0 ` +
  `M${x - r - 4} ${y + r + 16} Q${x} ${y + r + 4} ${x + r + 4} ${y + r + 16}`;

/* ---------- Stage map (compact) ---------- */
export const StageMap = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 420 250" className={className} animate={animate}>
    <g {...rough} className="text-pencil">
      {[0,1,2,3,4].map((i)=>(<P key={i} d={person(120 + i*46, 34, 8)} strokeWidth="1.6" />))}
    </g>
    <Hand x={210} y={16} anchor="middle" color="text-ink" size={14}>AUDIENCE</Hand>
    <g {...rough}>
      <P d="M120 80 h180 v110 h-180 Z" strokeWidth="2" />
      <P d="M180 80 v110 M240 80 v110 M120 117 h180 M120 154 h180" strokeWidth="1.2" className="text-pencil" />
    </g>
    <g {...rough} className="text-terracotta">
      <P d="M170 122 L280 92" strokeDasharray="1 7" strokeWidth="1.6" />
      <P d="M280 92 l-13 1 l4 12" strokeWidth="1.6" />
    </g>
    <Hand x={300} y={150} color="text-ink" size={18}>plan your path</Hand>
  </Svg>
);

/* ---------- Stage map (chapters, larger) ---------- */
export const StageMapChapters = ({ className = "", animate = true }) => {
  const gx = 150, gy = 150, cell = 78;
  const cx = gx + cell * 1.5, cy = gy + cell * 1.5;
  const nums = [1,2,3,4,5,6,7,8,9];
  return (
    <Svg viewBox="0 0 560 470" className={className} animate={animate}>
      <g {...rough} className="text-pencil">
        {[0,1,2,3,4].map((i)=>(<P key={i} d={person(gx + 30 + i*45, 66, 9)} strokeWidth="1.8" />))}
      </g>
      <Hand x={gx+117} y={40} color="text-ink" anchor="middle" size={18}>AUDIENCE</Hand>
      <g {...rough}>
        <P d={`M${gx} ${gy} h${cell*3} v${cell*3} h${-cell*3} Z`} strokeWidth="2.2" />
        <P d={`M${gx+cell} ${gy} v${cell*3}`} strokeWidth="1.5" className="text-pencil" />
        <P d={`M${gx+cell*2} ${gy} v${cell*3}`} strokeWidth="1.5" className="text-pencil" />
        <P d={`M${gx} ${gy+cell} h${cell*3}`} strokeWidth="1.5" className="text-pencil" />
        <P d={`M${gx} ${gy+cell*2} h${cell*3}`} strokeWidth="1.5" className="text-pencil" />
      </g>
      {nums.map((n,i)=>{
        const col = i%3, row = Math.floor(i/3);
        const ncx = gx + col*cell + cell/2, ncy = gy + row*cell + cell/2;
        if (n===5) return null;
        return (
          <g key={n}>
            <motion.circle variants={drawPath} cx={ncx} cy={ncy} r="13" {...base} strokeWidth="1.8" className="text-ink-soft" filter="url(#pencil-rough)" />
            <text x={ncx} y={ncy+6} textAnchor="middle" className="font-hand fill-current text-ink" style={{fontSize:18}}>{n}</text>
          </g>
        );
      })}
      {/* center marker (X — where the presenter stands) */}
      <g {...roughStrong} className="text-ink">
        <P d={`M${cx-14} ${cy-14} L${cx+14} ${cy+14}`} strokeWidth="2.4" />
        <P d={`M${cx+14} ${cy-14} L${cx-14} ${cy+14}`} strokeWidth="2.4" />
        <motion.circle variants={drawPath} cx={cx} cy={cy} r="22" {...base} strokeWidth="1.6" className="text-terracotta" filter="url(#pencil-rough)" />
      </g>
      <Hand x={cx+30} y={cy-22} color="text-terracotta" size={16}>you</Hand>
      <g {...rough} className="text-terracotta">
        <P d={`M${cx+16} ${cy-16} L${gx+cell*3-14} ${gy+16}`} strokeWidth="1.8" strokeDasharray="1 8" />
        <P d={`M${cx-16} ${cy+16} L${gx+16} ${gy+cell*3-14}`} strokeWidth="1.8" strokeDasharray="1 8" />
        <P d={`M${cx-16} ${cy} L${gx+16} ${cy}`} strokeWidth="1.8" strokeDasharray="1 8" />
        <P d={`M${cx} ${cy+18} L${cx} ${gy+cell*3-10}`} strokeWidth="1.8" strokeDasharray="1 8" />
      </g>
      <g {...rough} className="text-pencil">
        <P d={`M${gx+30} ${gy+cell*3+30} l0 -18 l-6 8 m6 -8 l6 8`} strokeWidth="1.6" />
        <P d={`M${gx+cell*3-30} ${gy+cell*3+30} l0 -18 l-6 8 m6 -8 l6 8`} strokeWidth="1.6" />
      </g>
      <Hand x={cx} y={gy+cell*3+30} anchor="middle" color="text-ink" size={16}>STAGE FRONT</Hand>
    </Svg>
  );
};

/* ---------- Voice wave / breath / audience / script / movement ---------- */
export const VoiceWave = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 380 230" className={className} animate={animate}>
    <g {...rough} className="text-ink-blue">
      {[0,1,2].map((i)=>(
        <P key={i} d={`M40 130 q${34+i*18} -${24+i*18} 0 -${46+i*36}`} strokeWidth="1.8" transform={`translate(${i*4} ${i*4})`} />
      ))}
    </g>
    <g {...rough} className="text-terracotta">
      <P d="M40 190 q14 -32 28 0 q14 32 28 0 q14 -42 28 0 q14 42 28 0 q14 -24 28 0 q14 24 28 0 q14 -32 28 0" strokeWidth="2.2" />
    </g>
    <Hand x={110} y={64} color="text-ink" size={20}>pace · pause · pitch</Hand>
  </Svg>
);

export const BreathDiagram = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 340 300" className={className} animate={animate}>
    <g {...roughStrong}>
      <P d="M120 74 Q170 58 220 74" />
      <P d="M116 74 Q100 154 130 214 Q170 236 210 214 Q240 154 224 74" />
      {[104,126,148,170].map((y,i)=>(<P key={i} d={`M124 ${y} Q170 ${y+14} 216 ${y}`} strokeWidth="1.4" className="text-pencil" />))}
    </g>
    <g {...rough} className="text-ink-blue">
      <P d="M232 118 q20 -8 40 0" strokeWidth="2" />
      <P d="M272 118 l-10 -5 m10 5 l-9 6" strokeWidth="2" />
      <P d="M232 150 q20 8 40 0" strokeWidth="2" />
      <P d="M232 150 l10 -5 m-10 5 l9 6" strokeWidth="2" />
    </g>
    <Hand x={252} y={104} color="text-ink-blue" size={18}>inhale</Hand>
    <Hand x={252} y={178} color="text-pencil" size={18}>exhale</Hand>
    <Hand x={106} y={260} color="text-ink" size={20}>breath = your power source</Hand>
  </Svg>
);

export const MovementPath = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 320 200" className={className} animate={animate}>
    <g {...rough} className="text-terracotta">
      <P d="M30 168 C90 150 120 90 200 70 C240 60 270 56 296 44" strokeDasharray="1 9" strokeWidth="2" />
      <P d="M296 44 l-15 0 l6 13" strokeWidth="2" />
    </g>
    <g {...roughStrong} className="text-ink">
      <P d="M40 176 q-6 -12 4 -16 q10 -3 10 8 q0 8 -6 10 Z" strokeWidth="1.6" />
      <P d="M120 120 q-6 -12 4 -16 q10 -3 10 8 q0 8 -6 10 Z" strokeWidth="1.6" />
      <P d="M210 86 q-6 -12 4 -16 q10 -3 10 8 q0 8 -6 10 Z" strokeWidth="1.6" />
    </g>
    <Hand x={150} y={40} color="text-ink" size={20}>move with purpose</Hand>
  </Svg>
);

export const AudienceSketch = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 460 260" className={className} animate={animate}>
    <g {...rough} className="text-pencil">
      {[0,1,2].map((row)=>{
        const y = 120 + row*50, count = 6+row*2, spread = 300+row*40, start = 230-spread/2, gap = spread/(count-1);
        return Array.from({length:count}).map((_,i)=>(<P key={`${row}-${i}`} d={person(start+i*gap, y, 9+row)} strokeWidth="1.8" />));
      })}
    </g>
    <Hand x={300} y={224} color="text-ink" size={20}>speak to one person</Hand>
  </Svg>
);

export const ScriptLines = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 360 220" className={className} animate={animate}>
    <g {...rough} className="text-pencil">
      {[42,76,110,144,178].map((y,i)=>(<P key={i} d={`M30 ${y} q80 ${i%2?6:-6} 150 0 q60 4 ${120-i*10} 0`} strokeWidth="1.8" />))}
    </g>
    <rect x="28" y="99" width="210" height="18" fill="rgba(216,155,60,0.4)" style={{mixBlendMode:"multiply"}} />
    <g {...rough} className="text-terracotta">
      <P d="M300 102 q28 4 40 20" strokeWidth="1.8" />
      <P d="M340 122 l-13 -1 l3 -12" strokeWidth="1.8" />
    </g>
    <Hand x={292} y={98} color="text-terracotta" size={20}>land this line</Hand>
    <Hand x={30} y={26} color="text-ink" size={18}>the script</Hand>
  </Svg>
);

export const Curtains = ({ className = "", animate = true }) => (
  <Svg viewBox="0 0 320 220" className={className} animate={animate}>
    <g {...roughStrong} className="text-ink">
      <P d="M20 10 h280 M40 10 Q30 110 46 200 M80 10 Q70 120 88 200 M120 10 Q110 110 126 190" />
      <P d="M300 10 Q302 110 286 200 M262 10 Q272 120 254 200 M222 10 Q232 110 216 190" />
      <P d="M126 190 Q160 176 154 120 M216 190 Q182 176 190 120" />
    </g>
    <path d="M158 40 L120 200 L212 200 L176 40 Z" fill="rgba(216,155,60,0.16)" stroke="none" />
  </Svg>
);
